import React, { useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { TabItem, Tab, Separator } from '@/config/navigation';
import { EditableContent } from '../EditableContent';

export function MobileMenu({ tabs, onClose }: { tabs: TabItem[]; onClose: () => void }) {
    const [expandedItems, setExpandedItems] = useState<number[]>([]);

    const toggleExpanded = (idx: number) => {
        setExpandedItems(prev =>
            prev.includes(idx)
                ? prev.filter(i => i !== idx)
                : [idx] // Only keep the current item expanded, close all others
        );
    };

    return (
        <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed top-0 right-0 w-80 h-screen z-50 p-4 shadow-lg bg-white dark:bg-black"
        >
            <button
                onClick={onClose}
                aria-label="Close menu"
                className="self-end mb-4 text-gray-600 dark:text-gray-300 hover:text-[#ED2245] text-xl"
            >
                ✕
            </button>
            <nav className="flex flex-col space-y-1">
                {tabs.map((tab, idx) => {
                    if ((tab as Separator).type === "separator") {
                        return null; // Skip separators in mobile menu
                    }
                    const Icon = (tab as Tab).icon;
                    const tabData = tab as Tab;
                    const isExpanded = expandedItems.includes(idx);

                    if (tabData.dropdown && tabData.items) {
                        return (
                            <div key={idx} className="border-b border-slate-100 dark:border-slate-800 pb-2">
                                <button
                                    onClick={() => toggleExpanded(idx)}
                                    className="w-full flex items-center justify-between gap-3 text-lg text-slate-700 dark:text-slate-200 hover:text-[#ED2245] font-semibold py-3 px-2"
                                >
                                    <div className="flex items-center gap-3">
                                        <Icon className="w-6 h-6" />
                                        {tabData.key ? <EditableContent id={tabData.key} showEditIcon={true} /> : tabData.title}
                                    </div>
                                    <svg
                                        className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <AnimatePresence>
                                    {isExpanded && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="ml-9 space-y-1 pt-2">
                                                {tabData.items.map((item) => (
                                                    <Link
                                                        key={item.id}
                                                        href={item.url}
                                                        prefetch={false}
                                                        onClick={onClose}
                                                        className="block text-sm text-slate-600 dark:text-slate-300 hover:text-[#ED2245] py-2 px-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800"
                                                    >
                                                        {item.key ? <EditableContent id={item.key} showEditIcon={true} /> : item.title}
                                                    </Link>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    }
 
                    return (
                        <Link
                            key={idx}
                            href={(tab as Tab).url || '/'}
                            prefetch={false}
                            onClick={() => {
                                if ('onClick' in tab && tab.onClick) tab.onClick();
                                onClose();
                            }}
                            className="flex items-center gap-3 text-lg text-slate-700 dark:text-slate-200 hover:text-[#ED2245] font-semibold py-3 px-2 border-b border-slate-100 dark:border-slate-700"
                        >
                            <Icon className="w-6 h-6" />
                            {tabData.key ? <EditableContent id={tabData.key} showEditIcon={true} /> : tabData.title}
                        </Link>
                    );
                })}
            </nav>
        </motion.div>
    );
}
